import React, { useState, useEffect } from 'react';
import './UserWiseReportPage.css';
import { getVehicleTypes, getLocations, getUserWiseReport, exportToExcel } from '../../api/apiClient';
import tooltipTexts from '../../utils/tooltipTexts';

const UserWiseReportPage = () => {
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState('');
  const [selectedSession, setSelectedSession] = useState('');
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [locations, setLocations] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [isLoadingEquipment, setIsLoadingEquipment] = useState(false);
  const [vehicleError, setVehicleError] = useState(null);
  const [maxAttemptNumber, setMaxAttemptNumber] = useState(0);

  // Set up initial data when component mounts
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user?.locationId) {
      setSelectedLocation(user.locationId);
      fetchLocations(user.locationId);
      fetchEquipmentTypes(user.locationId);
    } else {
      console.error('No location ID found for admin');
    }
  }, []);

  // Fetch equipment types when location changes
  useEffect(() => {
    if (selectedLocation) {
      setSelectedEquipment('');
      setVehicleError(null);
      setEquipmentTypes([]);
      fetchEquipmentTypes(selectedLocation);
    } else {
      setEquipmentTypes([]);
      setVehicleError(null);
    }
  }, [selectedLocation]);

  // Update session dropdown when equipment changes
  useEffect(() => {
    if (selectedEquipment) {
      // Reset session selection when vehicle changes
      setSelectedSession('');
      setMaxAttemptNumber(0);
      
      // Fetch report data to determine highest attempt number
      setIsLoading(true);
      const fetchVehicleData = async () => {
        try {
          const filters = {
            locationId: selectedLocation,
            vehicleId: selectedEquipment
          };
          
          const data = await getUserWiseReport(filters);
          
          if (data && data.length > 0) {
            // Find the highest attempt number for this vehicle
            const highestAttempt = Math.max(...data.map(item => 
              typeof item.AttemptNumber === 'string' 
                ? parseInt(item.AttemptNumber) 
                : item.AttemptNumber || 0
            ));
            setMaxAttemptNumber(highestAttempt);
          } else {
            setMaxAttemptNumber(0);
          }
        } catch (err) {
          console.error('Error fetching vehicle data:', err);
          setMaxAttemptNumber(0);
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchVehicleData();
    }
  }, [selectedEquipment, selectedLocation]);

  const fetchLocations = async (adminLocationId) => {
    try {
      const locations = await getLocations(adminLocationId);
      console.log('Locations fetched:', locations);
      setLocations(locations);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchEquipmentTypes = async (locationId) => {
    setIsLoadingEquipment(true);
    setVehicleError(null);
    setEquipmentTypes([]);
    
    try {
      console.log(`Fetching vehicles for location ID: ${locationId}`);
      const data = await getVehicleTypes(locationId);
      
      console.log(`Received vehicles for location ${locationId}:`, data);
      
      if (data.length === 0) {
        const selectedLocationName = locations.find(loc => 
          String(loc.LocationID || loc.locationID || loc.locationId || loc.id) === String(locationId)
        )?.LocationName || 'Selected location';
        
        setVehicleError(`${selectedLocationName} has no registered vehicles`);
      } else {
        setEquipmentTypes(data);
      }
    } catch (error) {
      console.error('Error fetching equipment types:', error);
      setVehicleError('Failed to load vehicles for this location');
    } finally {
      setIsLoadingEquipment(false);
    }
  };

  const fetchReportData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const filters = {
        locationId: selectedLocation,
        vehicleId: selectedEquipment,
        session: selectedSession
      };
      
      const data = await getUserWiseReport(filters);
      
      // Filter data based on selected session if a session is selected
      const filteredData = selectedSession 
        ? data.filter(item => {
            const itemAttempt = typeof item.AttemptNumber === 'string' 
              ? parseInt(item.AttemptNumber) 
              : item.AttemptNumber;
            return itemAttempt === parseInt(selectedSession);
          })
        : data;
      setReportData(filteredData);
    } catch (err) {
      setError('Failed to load report data. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Generate session options based on highest attempt number
  const generateSessionOptions = () => {
    const options = [<option key="all" value="">All Sessions</option>];
    
    for (let i = 1; i <= maxAttemptNumber; i++) {
      options.push(
        <option key={i} value={i.toString()}>Session {i}</option>
      );
    }
    
    return options;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchReportData();
  };

  const handleExportExcel = () => {
    if (reportData.length === 0) {
      alert('No data to export');
      return;
    }

    // Get the selected vehicle name if available
    let selectedVehicleName = 'All';
    if (selectedEquipment && selectedEquipment !== '') {
      const foundVehicle = equipmentTypes.find(v => String(v.VehicleID) === String(selectedEquipment));
      if (foundVehicle) {
        selectedVehicleName = foundVehicle.ScreenName || foundVehicle.VehicleName || 'Unknown';
      }
    }

    // Filter data based on selected session if one is selected
    const dataToExport = selectedSession 
      ? reportData.filter(item => item.AttemptNumber === parseInt(selectedSession))
      : reportData;

    // Prepare headers and rows for user-wise report
    const headers = [
      'Sl No.',
      'Username',
      'Location',
      'Mobile Equipment Type',
      'Session',
      'Driver Score Category',
      'Score'
    ];
    const csvContent = [
      headers.join(','),
      ...dataToExport.map((item, index) =>
        [
          index + 1,
          `"${item.EmployeeName}"`,
          `"${item.LocationName}"`,
          `"${item.ScreenName || item.VehicleName || getVehicleDisplayName(item.VehicleID)}"`,
          item.AttemptNumber,
          `"${item.DriverScoreCategory}"`,
          item.AverageScore
        ].join(',')
      )
    ].join('\n');

    // Create a blob and download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const sessionSuffix = selectedSession ? ` - Session ${selectedSession}` : '';
    const fileName = `${selectedVehicleName} User Wise Report${sessionSuffix}`;
    link.setAttribute('download', fileName + '.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get vehicle display name by ID
  const getVehicleDisplayName = (vehicleId) => {
    if (!vehicleId) return 'Unknown Vehicle';
    
    const vehicle = equipmentTypes.find(v => v && String(v.VehicleID) === String(vehicleId));
    return vehicle ? (vehicle.ScreenName || vehicle.VehicleName || `Vehicle ${vehicleId}`) : `Vehicle ${vehicleId}`;
  };

  return (
    <div className="user-wise-report-page">
      <div className="page-header">
        <h2>
          User Wise Report Page
          <span className="header-tooltip">
            <span className="info-icon">i</span>
            <span className="tooltip-text">{tooltipTexts.userWiseReportHeader}</span>
          </span>
        </h2>
      </div>
      <div className="content-area">
        <div className="filter-section">
          <div className="dropdown-container">
            <div className="select-wrapper">
              <select 
                value={selectedLocation} 
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="location-select"
                disabled={true}
              >
                {locations.map(location => (
                  <option key={location.LocationID || location.id} value={location.LocationID || location.id}>
                    {location.LocationName || location.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="select-wrapper">
              <select 
                value={selectedEquipment} 
                onChange={(e) => setSelectedEquipment(e.target.value)}
                className="equipment-select"
                disabled={isLoadingEquipment || !selectedLocation || vehicleError !== null}
              >
                {isLoadingEquipment ? (
                  <option value="">Loading equipment types...</option>
                ) : !selectedLocation ? (
                  <option value="">Select Location First</option>
                ) : vehicleError ? (
                  <option value="">{vehicleError}</option>
                ) : equipmentTypes.length === 0 ? (
                  <option value="">No equipment types available</option>
                ) : (
                  <>
                    <option value="">Select Mobile Equipment Type*</option>
                    {equipmentTypes.map(vehicle => (
                      <option key={vehicle.VehicleID} value={vehicle.VehicleID}>
                        {vehicle.ScreenName || vehicle.VehicleName || `Vehicle ${vehicle.VehicleID}`}
                      </option>
                    ))}
                  </>
                )}
              </select>
              {vehicleError && (
                <div className="error-message equipment-error">{vehicleError}</div>
              )}
            </div>

            <div className="select-wrapper">
              <select
                value={selectedSession}
                onChange={(e) => setSelectedSession(e.target.value)}
                className="session-select"
                disabled={isLoading || !selectedEquipment}
              >
                {generateSessionOptions()}
              </select>
            </div>
            
            <button className="export-button" onClick={handleExportExcel}>
              <span>Export Excel Sheet</span> 
              <span className="export-icon">↓</span>
            </button>
          </div>
          
          <button 
            className="submit-button" 
            onClick={handleSubmit}
            disabled={isLoading || !selectedLocation || !selectedEquipment || selectedEquipment === "" || (vehicleError !== null)}
          >
            {isLoading ? 'Loading...' : 'Submit'}
          </button>
        </div>
        
        <div className="report-table-container">
          {isLoading ? (
            <div className="loading-container">Loading report data...</div>
          ) : error ? (
            <div className="error-container">{error}</div>
          ) : (
            <table className="report-table">
              <thead>
                <tr>
                  <th className="sl-col">Sl No.</th>
                  <th className="username-col">Username</th>
                  <th className="location-col">Location</th>
                  <th className="equipment-col">Mobile Equipment Type</th>
                  <th className="session-col">Session</th>
                  <th className="category-col">Driver Score Category</th>
                  <th className="score-col">Score</th>
                </tr>
              </thead>
              <tbody>
                {reportData.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center' }}>No data available</td>
                  </tr>
                ) : (
                  reportData.map((row, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                      <td>{index + 1}</td>
                      <td>{row.EmployeeName}</td>
                      <td>{row.LocationName}</td>
                      <td>{row.ScreenName || row.VehicleName || getVehicleDisplayName(row.VehicleID)}</td>
                      <td>{row.AttemptNumber}</td>
                      <td>
                        <span className={`score-category ${row.DriverScoreCategory.toLowerCase()}`}>
                          {row.DriverScoreCategory}
                        </span>
                      </td>
                      <td>{row.AverageScore}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserWiseReportPage; 